import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { flyToCart } from "../../../shared/animations";
import { useCartContext } from "../../../shared/context/cart.context";
import {
  categoryDescriptionMap,
  getProductsByCategory,
  menuSections,
} from "../../../shared/data";
import { useMenuData } from "../../../shared/context/menu-data.context";
import { MenuLoader } from "../menu-loader/menu-loader";
import {
  CategoriesModel,
  ProductModel,
  SectionKey,
  SubCategoriesEntries,
} from "../../../shared/data/categories.model";
import * as styles from "./new-menu-section.module.scss";
import { NewMenuItem } from "../new-menu-item";

const buildClassName = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

type CategoriesWithDescription = CategoriesModel & { description?: string };

interface NewMenuSectionProps {
  sectionKey: SectionKey;
}

export const NewMenuSection: FC<NewMenuSectionProps> = ({ sectionKey }) => {
  const [scrollActive, setScrollActive] = useState<string | null>(null);
  const { addSingleProduct, cartRef } = useCartContext();
  const { products, restaurantProducts, categories: categoriesData, isLoading } = useMenuData();
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLUListElement>(null);

  const { t } = useTranslation();

  const section = useMemo(
    () => menuSections.find((section) => section.id === sectionKey),
    [sectionKey],
  );

  const categories: CategoriesWithDescription[] = useMemo(() => {
    const res = section
      ? categoriesData.filter((item) =>
          section.categories.includes(item.categoryKey),
        )
      : [];

    return res.map((item) => ({
      ...item,
      description: categoryDescriptionMap.get(item.categoryKey),
    }));
  }, [section, categoriesData]);

  const productsMap: Map<SubCategoriesEntries, ProductModel[]> = useMemo(() => {
    const map = new Map<SubCategoriesEntries, ProductModel[]>();
    categories.forEach((item) => {
      map.set(item.categoryKey, getProductsByCategory(products, restaurantProducts, item.categoryKey));
    });
    return map;
  }, [categories, products, restaurantProducts]);

  const isSectionRestaurant = useMemo(
    () => sectionKey === SectionKey.RESTAURANT,
    [sectionKey],
  );

  // Initialize active category
  useEffect(() => {
    if (categories.length) {
      setScrollActive(categories[0].categoryKey);
    }
  }, [categories]);

  // Scroll tracking
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleScroll = () => {
      if (!headerRef.current) return;

      const headerHeight = headerRef.current.offsetHeight;
      const containerTop =
        container.getBoundingClientRect().top + window.scrollY;
      const scrollPosition = window.scrollY + headerHeight - containerTop;

      let currentId: string | null = null;
      categories.forEach((category) => {
        const el = document.getElementById(category.categoryKey);
        if (el) {
          const relativeTop = el.offsetTop - container.offsetTop;
          if (relativeTop - 1 < scrollPosition) {
            currentId = category.categoryKey;
          }
        }
      });

      if (currentId && currentId !== scrollActive) {
        setScrollActive(currentId);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initialize

    return () => window.removeEventListener("scroll", handleScroll);
  }, [categories, scrollActive]);

  const scrollToCategory = (categoryName: string) => {
    if (!containerRef.current || !headerRef.current) return;

    const container = containerRef.current;
    const el = document.getElementById(categoryName);
    if (!el) return;

    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const headerHeight = headerRef.current.offsetHeight;
    const offsetTop = el.offsetTop - container.offsetTop - headerHeight;

    window.scrollTo({ top: containerTop + offsetTop, behavior: "smooth" });
  };

  if (isLoading) {
    return <MenuLoader />;
  }

  return (
    <div
      ref={containerRef}
      className={buildClassName(styles.tab__bodyItem, styles.active)}
    >
      {/* {categories.length > 1 && (
        <ul ref={headerRef} className={styles.tab__headerSubsection}>
          {categories.map((category) => (
            <li
              key={category.id}
              className={buildClassName(
                styles.tab__subsection,
                scrollActive === category.categoryKey && styles.active
              )}
            >
              <a
                className={styles.tab__subsectionA}
                href={`#${category.categoryKey.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToCategory(category.categoryKey);
                }}
              >
                {t(`menu.categories.${category.categoryKey}`)}
              </a>
            </li>
          ))}
        </ul>
      )} */}

      <div className={styles.tab__bodyContainer}>
        {sectionKey === SectionKey.RESTAURANT && (
          <div className={styles.restaurantNotice}>
            <p className={styles.restaurantNotice__title}>
              {t("menu.restaurantNotice.title")}
            </p>
            <p className={styles.restaurantNotice__description}>
              {t("menu.restaurantNotice.description")}
            </p>
            <p className={styles.restaurantNotice__checkItems}>
              {t("menu.restaurantNotice.checkItems")}
            </p>
            <div className={styles.restaurantNotice__links}>
              <a
                href="/menu/menu_pdf/daigo-menu-02-11-2026_EN.pdf"
                download
                className={styles.restaurantNotice__link}
              >
                {t("menu.restaurantNotice.menuLinkEn")}
              </a>
              <a
                href="/menu/menu_pdf/daigo-menu-02-11-2026_PL.pdf"
                download
                className={styles.restaurantNotice__link}
              >
                {t("menu.restaurantNotice.menuLinkPl")}
              </a>
            </div>
          </div>
        )}

        {categories.map((category, index) => (
          <div
            key={category.id}
            id={category.categoryKey}
            className={styles.subsection__id}
          >
            <div className={styles.subsection__info}>
              <h3 className={styles.subsection__title}>
                {t(`menu.categories.${category.categoryKey}`)}

                {category.count && (
                  <span className={styles.subsection__count}>
                    &nbsp;({category.count} {t("menu.count")})
                  </span>
                )}
              </h3>

              {sectionKey === SectionKey.SETS_SPECIAL && index === 0 && (
                <p className={styles.setsSpecialNotice__description}>
                  {t("menu.newYearEveNotice.description")}
                </p>
              )}

              {category.description && (
                <p className={styles.subsection__descript}>
                  {t(category.description)}
                </p>
              )}
            </div>
            <div className={styles.subsection__body}>
              {productsMap.get(category.categoryKey)?.map((product, i) => (
                <NewMenuItem
                  data-follow
                  data-only-restaurant={
                    product.onlyRestaurant || isSectionRestaurant
                      ? "true"
                      : undefined
                  }
                  key={i}
                  product={product}
                  onClick={(e) => {
                    if (isSectionRestaurant || product.onlyRestaurant) return;

                    const productEl = e.currentTarget as HTMLElement;
                    addSingleProduct(product.id);

                    productEl.classList.add(styles.active);

                    if (cartRef.current) {
                      flyToCart(productEl, cartRef.current, () => {});
                    }

                    setTimeout(() => {
                      productEl.classList.remove(styles.active);
                    }, 300);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
